import { JsonRestApiRequestConfig, makeJsonRestApiRequest } from '~src/httpClient/RestApiService';
import { Request } from '~src/httpClient/serverApi/requests/Request';
import { IGetItemsParams } from '~src/httpClient/serverApi/types/IGetItemsParams';
import { IGetItemsResult } from '~src/httpClient/serverApi/types/IGetItemsResult';
import { SelfServiceSchedule } from '~src/schedule/types/SelfServiceSchedule';
import { SelfServiceScheduleValidator } from '~src/schedule/utils/SelfServiceScheduleValidator';
import { IIdItem, IScheduledItem, ISiteItem } from '~src/spacesPlatform/models/resourceParts';


// REST API: add/update task

function getAddUpdateGeocodingTaskConfig(): JsonRestApiRequestConfig {
  return {
    restApiVersion: '3.25',
    httpMethod: 'POST',
    endpoint: `tasks/geocoding`,
    serverWideRequest: false,
  };
}

export type AddUpdateGeocodingTaskRequestConfigParams = {
  schedule: SelfServiceSchedule;
  geocodingConfigId: string;
}

export enum GeocodingTaskType {
  RunAddressGeocodingTask = 'RunAddressGeocodingTask',
  EstimateAddressGeocodingTask = 'EstimateAddressGeocodingTask',
}

export enum GeocodingJobStatus {
  Pending = 'Pending',
  InProgress = 'InProgress',
  Success = 'Success',
  Cancelled = 'Cancelled',
  Failed = 'Failed'
}

export type GeocodingJob = {
  id: string;
  geocodingConfig: string;
  createdAt?: string;
  startedAt?: string;
  completedAt?: string;
  status?: GeocodingJobStatus;
};

export type GeocodingTask = {
  id: string;
  priority: number;
  consecutiveFailedCount: number;
  type: GeocodingTaskType;
  schedule: SelfServiceSchedule;
  geocodingConfig: string;
  lastRunJob?: GeocodingJob;
}

export type GeocodingTaskResponse = {
  readonly geocodingTask: GeocodingTask;
};

export function callAddUpdateGeocodingTaskViaApiService(request: AddUpdateGeocodingTaskRequestConfigParams) {
    SelfServiceScheduleValidator.validateRequest(request.schedule);
    return makeJsonRestApiRequest<AddUpdateGeocodingTaskRequestConfigParams, GeocodingTaskResponse>({
      config: getAddUpdateGeocodingTaskConfig(),
      request: request,
    });
}

/// REST API: Run task

export type RunGeocodingTaskRequestConfigParams = {
  readonly taskLuid: string;
}

function runTaskRequestConfig(params: RunGeocodingTaskRequestConfigParams): JsonRestApiRequestConfig {
  return {
    restApiVersion: '3.25',
    httpMethod: 'POST',
    endpoint: `tasks/geocoding/${params.taskLuid}/runNow`,
    serverWideRequest: false,
  };
}

export type GeocodingJobResponse = {
  readonly geocodingJob: GeocodingJob;
};

export function callRunGeocodingTaskViaApiService(params: RunGeocodingTaskRequestConfigParams) {
    return makeJsonRestApiRequest<void, GeocodingJobResponse>({
      config: runTaskRequestConfig(params),
      request: null,
    });
}



// REST API: get task

export type GetTaskRequestConfigParams = {
  readonly taskLuid: string;
}

function getTaskRequestConfig(params: GetTaskRequestConfigParams): JsonRestApiRequestConfig {
  return {
    restApiVersion: '3.25',
    httpMethod: 'GET',
    endpoint: `tasks/geocoding/${params.taskLuid}`,
    serverWideRequest: false,
  };
}

export function callGetTaskViaApiService(params: GetTaskRequestConfigParams) {
    return makeJsonRestApiRequest<void, GeocodingTaskResponse>({
      config: getTaskRequestConfig(params),
      request: null,
    });
}

// REST API: queue geocoding estimation job

export type QueueEstimationJobParams = {
  readonly configLuid: string;
}

function queueEstimationJob(params: QueueEstimationJobParams): JsonRestApiRequestConfig {
  return {
    restApiVersion: '3.25',
    httpMethod: 'GET',
    endpoint: `geocodingConfigs/${params.configLuid}/estimate`,
    serverWideRequest: false,
  };
}

export function callQueueEstimationJobViaApiService(params: QueueEstimationJobParams) {
    return makeJsonRestApiRequest<void, GeocodingJobResponse>({
      config: queueEstimationJob(params),
      request: null,
    });
}

// WebClient API: get all geocoding tasks from site for the Geocoding tab on the Tasks Page

export interface IGeocodingTask extends IIdItem, IScheduledItem, ISiteItem {
  id: string;  // task luid
  lastRunAt?: string; // YYYY-MM-DDThh:mm:ssZ
  priority: number;
  runNextAt?: string; // YYYY-MM-DDThh:mm:ssZ
  unifiedDataConnectionId: string;
  state: string;
  targetId: string;
  configLuid: string;
  configName: string;
  scheduleId: string;
  siteId: string;
}


export interface GetGeocodingTasksResult extends IGetItemsResult {
  tasks: IGeocodingTask[];
}

export class GetGeocodingTasksRequest extends Request<IGetItemsParams, GetGeocodingTasksResult> {
  constructor(params: IGetItemsParams) {
    super('getGeocodingTasks', params);
  }
}
